import { API_BASE } from './core';

// ============ HEADER ALIAS CONFIGURATION ============
// Aliases collapse to these normalized forms (lowercase, whitespace-stripped).
// Examples: "PROJECTID", "Project", "Project ID" → "projectid", "project", "projectid"
const normalizeHeader = (s) => String(s ?? '').toLowerCase().replace(/\s+/g, '').trim();

const HEADER_ALIASES = {
  PROJECT:  ['projectid', 'project'],
  COSTCODE: ['taskid', 'costcode', 'costcodeid'],
  COSTTYPE: ['costtypeid', 'costtype'],
};

const HEADER_LABELS = {
  PROJECT:  'Project',
  COSTCODE: 'Cost Code (Task)',
  COSTTYPE: 'Cost Type',
};

// ============ DETECTION ============
// Returns { mapping: { PROJECT, COSTCODE, COSTTYPE } } where values are column indices,
// or { error: '...' } if any required column is missing.
export const detectCostStructureHeaders = (rawHeaders) => {
  if (!rawHeaders || !Array.isArray(rawHeaders)) {
    return { error: 'No headers found in selection.' };
  }
  const mapping = {};
  rawHeaders.forEach((h, idx) => {
    const norm = normalizeHeader(h);
    for (const [canonical, aliases] of Object.entries(HEADER_ALIASES)) {
      if (aliases.includes(norm) && mapping[canonical] === undefined) {
        mapping[canonical] = idx;
      }
    }
  });
  const missing = Object.keys(HEADER_ALIASES).filter(k => mapping[k] === undefined);
  if (missing.length > 0) {
    return { error: `Missing required column(s): ${missing.map(m => HEADER_LABELS[m]).join(', ')}.` };
  }
  return { mapping };
};

// Transform Excel data rows + header mapping → array of { PROJECT, COSTCODE, COSTTYPE }
// Skips entirely-blank rows and rows missing any of the three required values.
export const extractCostStructureRows = (mapping, dataRows) => {
  const rows = [];
  for (const row of dataRows) {
    if (!row || row.every(c => c === '' || c == null)) continue;
    const project  = String(row[mapping.PROJECT]  ?? '').trim();
    const costCode = String(row[mapping.COSTCODE] ?? '').trim();
    const costType = String(row[mapping.COSTTYPE] ?? '').trim();
    if (!project || !costCode || !costType) continue;
    rows.push({ PROJECT: project, COSTCODE: costCode, COSTTYPE: costType });
  }
  return rows;
};

// ============ API CALLS ============
const postPurchasing = async (path, body) => {
  const resp = await fetch(`${API_BASE}/api/purchasing/${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  let data;
  try { data = await resp.json(); }
  catch (e) { throw new Error(`Server returned ${resp.status}: ${resp.statusText}`); }
  if (!resp.ok || data.error) {
    throw new Error(data?.error || `Server returned ${resp.status}: ${resp.statusText}`);
  }
  return data;
};

// Returns { totalRequired, found, missingTasks, missingCostTypes, existingCount }
export const validateCostStructure = async (config, rows) => {
  return postPurchasing('validate-cost-structure', { credentials: config, rows });
};

// Returns array of { project, costCode, status: 'created'|'error', method?, error? }
export const createCostStructureTasks = async (config, missingTasks) => {
  const data = await postPurchasing('create-tasks', { credentials: config, missingTasks });
  return data.results || [];
};

// Returns array of { project, costCode, costType, status: 'created'|'error', error? }
export const createCostStructureCostTypes = async (config, missingCostTypes) => {
  const data = await postPurchasing('create-cost-types', { credentials: config, missingCostTypes });
  return data.results || [];
};